import { useCallback, useEffect, useState } from 'react';
import { gymsService } from '../../services/gyms';
import type { Gym } from '../../types/gym';
import { useGymAdmin } from '../context/gym-admin-context';

/**
 * Loads the full Gym record for the gym currently selected in the sidebar.
 * Refetches whenever the selection changes, and exposes `setGym` so pages can
 * apply optimistic/local updates after saving.
 */
export function useCurrentGym() {
  const { selectedGymSlug } = useGymAdmin();
  const [gym, setGym] = useState<Gym | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await gymsService.getGymBySlug(slug);
      setGym(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load gym');
      setGym(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedGymSlug) {
      setGym(null);
      setLoading(false);
      return;
    }
    load(selectedGymSlug);
  }, [selectedGymSlug, load]);

  const reload = useCallback(() => {
    if (selectedGymSlug) load(selectedGymSlug);
  }, [selectedGymSlug, load]);

  return { gym, loading, error, setGym, reload, slug: selectedGymSlug };
}
